import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import io from 'socket.io-client';
import Peer from 'peerjs';
import toast from 'react-hot-toast';
import {
  VideoCameraIcon,
  MicrophoneIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  PaperClipIcon,
  PaperAirplaneIcon,
  PhoneXMarkIcon,
  VideoCameraSlashIcon,
  SpeakerXMarkIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  FolderIcon,
  PencilIcon,
  ComputerDesktopIcon,
  XMarkIcon,
  CalendarDaysIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MinusIcon,
  Bars3Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhoneIcon
} from '@heroicons/react/24/solid';
import axios from 'axios';
import FileUpload from './FileUpload';
import FileList from './FileList';
import Whiteboard from './Whiteboard';
import DocumentWorkspace from './DocumentWorkspace';
import Schedule from './Schedule';
import { useConfirm } from './ConfirmDialog';

const Classroom = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  
  // State management
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem(`classroom-tab-${id}`);
    const valid = ['chat', 'posts', 'files', 'whiteboard', 'schedule', 'students'];
    return valid.includes(saved) ? saved : 'chat';
  });
  const [participants, setParticipants] = useState([]);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [inCall, setInCall] = useState(false);
  const [peers, setPeers] = useState({});
  const [remoteVideoOff, setRemoteVideoOff] = useState({}); // userId -> true when that peer's camera is off
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenSharerId, setScreenSharerId] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [classroomData, setClassroomData] = useState(null);
  const [students, setStudents] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [sendingInvites, setSendingInvites] = useState(false);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [studentEmail, setStudentEmail] = useState('');

  // Responsive state - updates on window resize
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640); // Phone-sized screens
  const [isTabletOrSmaller, setIsTabletOrSmaller] = useState(window.innerWidth <= 1024);

  const [incomingCallNotification, setIncomingCallNotification] = useState(null);
  const [activeCallInClassroom, setActiveCallInClassroom] = useState(false); // Tracks if there's an active call (not dismissible)

  const [isCodeBannerExpanded, setIsCodeBannerExpanded] = useState(false);
  const [isVideoMinimized, setIsVideoMinimized] = useState(false);
  const [videoPosition, setVideoPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [showControls, setShowControls] = useState(true);
  // Start sidebar closed on laptop/tablet screens and open on larger screens
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1536);
  // Keep tab bar collapsed on tablets/mobile for maximum whiteboard space
  const [isTabBarExpanded, setIsTabBarExpanded] = useState(window.innerWidth > 1024);
  const [fileRefreshKey, setFileRefreshKey] = useState(0);
  const [socketConnected, setSocketConnected] = useState(null); // null = connecting, true = connected, false = disconnected
  const [unreadMessages, setUnreadMessages] = useState(0);
  const activeTabRef = useRef('chat');

  // Check if screen sharing is supported (not available on iOS/mobile browsers)
  const supportsScreenShare = Boolean(
    navigator.mediaDevices &&
    navigator.mediaDevices.getDisplayMedia &&
    !/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  );

  // Refs
  const socketRef = useRef();
  const peerRef = useRef();
  const localVideoRef = useRef();
  const localStreamRef = useRef();
  const screenShareStreamRef = useRef();
  const messagesEndRef = useRef();
  const videoContainerRef = useRef();
  const peersRef = useRef({});
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const pendingIncomingCalls = useRef([]);
  const controlsTimerRef = useRef(null);

  useEffect(() => {
    // Fullscreen change event listener
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Window resize listener for responsive layout
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTabletOrSmaller(width <= 1024);

      // Auto-close sidebar on mobile/tablet
      if (width < 1536) {
        setIsSidebarOpen(false);
      }

      // Collapse tab bar on tablet/mobile
      if (width <= 1024) {
        setIsTabBarExpanded(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Check enrollment authorization
  useEffect(() => {
    const checkAuthorization = async () => {
      try {
        setIsCheckingAuth(true);

        // Fetch classroom data
        const classroomResponse = await axios.get(`/api/classrooms`);
        const classroom = classroomResponse.data.find(c => c.id === id);

        if (!classroom) {
          toast.error('Classroom not found');
          navigate('/dashboard');
          return;
        }

        setClassroomData(classroom);

        // Check if user is admin - admins have full access to all classrooms
        if (user?.user_metadata?.role === 'admin') {
          console.log('Admin user - granting full classroom access');
          setIsAuthorized(true);
          setIsCheckingAuth(false);
          return;
        }

        // Check if user is the tutor
        if (classroom.tutor_id === user.id) {
          setIsAuthorized(true);
          setIsCheckingAuth(false);
          return;
        }

        // Check if user is enrolled
        const enrollmentsResponse = await axios.get(`/api/classrooms/${id}/students`);
        const isEnrolled = enrollmentsResponse.data.some(
          enrollment => enrollment.student_id === user.id
        );

        if (isEnrolled) {
          setIsAuthorized(true);
        } else {
          toast.error('You are not enrolled in this classroom');
          navigate('/dashboard');
        }

        setIsCheckingAuth(false);
      } catch (error) {
        console.error('Error checking authorization:', error);
        toast.error('Failed to verify classroom access');
        navigate('/dashboard');
      }
    };

    if (user && id) {
      checkAuthorization();
    }
  }, [id, user, navigate]);

  useEffect(() => {
    // Set local video stream when it becomes available
    if (localStreamRef.current && localVideoRef.current && inCall) {
      localVideoRef.current.srcObject = localStreamRef.current;
      localVideoRef.current.play().catch(error => {
        console.error('Error playing local video:', error);
      });
    }
  }, [inCall]);

  useEffect(() => {
    // Don't initialize if not authorized yet
    if (!isAuthorized || isCheckingAuth) {
      return;
    }
    // Initialize Socket.io connection
    socketRef.current = io(process.env.REACT_APP_API_URL || 'http://localhost:5000');

    // Initialize PeerJS with cloud server
    peerRef.current = new Peer(undefined, {
      host: '0.peerjs.com',
      port: 443,
      path: '/',
      secure: true
    });

    // Add current user to participants immediately
    const currentUser = {
      id: user.id,
      name: user.user_metadata?.name || user.email,
      role: user.user_metadata?.role || 'student',
      peerId: null // Will be set when peer connection opens
    };
    setParticipants([currentUser]);

    // (Re)register with the server on EVERY (re)connect — not just the first
    // time. Socket.io auto-reconnects after a network blip with a brand-new
    // socket id, so without re-emitting these the server no longer knows this
    // socket belongs to the classroom, and presence/chat/call signaling would
    // silently stay broken until a full page reload.
    const registerWithServer = () => {
      socketRef.current.emit('user-join', {
        id: user.id,
        name: user.user_metadata?.name || user.email,
        role: user.user_metadata?.role || 'student'
      });
      socketRef.current.emit('join-classroom', id);
      // Re-advertise our peer ID so peers keep/rebuild the video link.
      if (peerRef.current && peerRef.current.id && !peerRef.current.disconnected) {
        socketRef.current.emit('share-peer-id', {
          classroomId: id,
          userId: user.id,
          peerId: peerRef.current.id
        });
      }
      // If we're mid-call, re-announce our camera state to the room.
      if (localStreamRef.current) broadcastVideoState();
    };

    socketRef.current.on('connect', () => {
      setSocketConnected(true);
      registerWithServer();
    });
    socketRef.current.on('disconnect', () => setSocketConnected(false));

    // Socket event listeners
    socketRef.current.on('classroom-messages', (messages) => {
      setMessages(messages);
    });

    socketRef.current.on('new-message', (message) => {
      setMessages(prev => [...prev, message]);
      if (activeTabRef.current !== 'chat' && message.userId !== user.id) {
        setUnreadMessages(prev => prev + 1);
      }
      scrollToBottom();
    });

    // Receive existing participants already in the classroom
    socketRef.current.on('classroom-participants', (existingParticipants) => {
      setParticipants(prev => {
        const newParticipants = existingParticipants.filter(
          ep => !prev.some(p => p.id === ep.id)
        );
        return [...prev, ...newParticipants];
      });
    });

    socketRef.current.on('user-joined-classroom', ({ userId, userData }) => {
      setParticipants(prev => {
        // Don't add if already exists
        if (prev.some(p => p.id === userId)) return prev;
        return [...prev, { ...userData, peerId: null }];
      });
      toast.success(`${userData?.name || 'Someone'} joined the classroom`);
    });

    socketRef.current.on('user-left-classroom', ({ userId }) => {
      setParticipants(prev => {
        // Treat a leave/refresh as leaving the call: tear down their video tile
        // so it doesn't linger as a frozen "ghost". Once removed, a remaining
        // solo participant has no remote peers and their own video fills the
        // screen (grid collapses to a single column).
        const leaving = prev.find(p => p.id === userId);
        if (leaving?.peerId) removePeer(leaving.peerId);
        setRemoteVideoOff(prevOff => {
          if (!(userId in prevOff)) return prevOff;
          const next = { ...prevOff };
          delete next[userId];
          return next;
        });
        return prev.filter(p => p.id !== userId);
      });
    });

    // Listen for peer ID updates from other users
    socketRef.current.on('peer-id-shared', ({ userId, peerId }) => {
      setParticipants(prev =>
        prev.map(p => p.id === userId ? { ...p, peerId } : p)
      );
      // A peer just (re)connected — if we're in a call, tell them our current
      // camera state so a disabled camera shows as an avatar on their end too.
      if (localStreamRef.current) broadcastVideoState();
    });

    // A remote peer toggled their camera — track it so we can show their avatar
    // overlay instead of a black video tile.
    socketRef.current.on('peer-video-toggle', ({ userId, enabled }) => {
      setRemoteVideoOff(prev => ({ ...prev, [userId]: !enabled }));
    });

    // Listen for video call signaling events
    socketRef.current.on('video-call-started', ({ userId, userName }) => {
      // Always track that there's an active call in the classroom
      setActiveCallInClassroom(true);
      if (userId !== user.id && !inCall) {
        setIncomingCallNotification({ userName, userId });
      }
    });

    socketRef.current.on('video-call-ended', ({ userId, remainingCallers }) => {
      // Update active call state based on remaining callers
      if (remainingCallers && remainingCallers.length > 0) {
        setActiveCallInClassroom(true);
        // Show notification with the first remaining caller's info
        // This helps the user who just left to see they can rejoin
        const nextCaller = remainingCallers[0];
        setIncomingCallNotification({ userName: nextCaller.userName, userId: nextCaller.userId });
      } else {
        // No callers remain, clear everything
        setActiveCallInClassroom(false);
        setIncomingCallNotification(null);
      }
    });

    // Handle active call notification for late joiners
    socketRef.current.on('active-call', ({ userId, userName }) => {
      setActiveCallInClassroom(true);
      if (userId !== user.id && !inCall) {
        setIncomingCallNotification({ userName, userId });
      }
    });

    // Listen for screen sharing events
    socketRef.current.on('screen-share-started', ({ userId, userName }) => {
      setScreenSharerId(userId);
      if (userId !== user.id) {
        toast.success(`${userName} is sharing their screen`);
      }
    });

    socketRef.current.on('screen-share-stopped', ({ userId }) => {
      if (screenSharerId === userId) {
        setScreenSharerId(null);
      }
      if (userId !== user.id) {
        toast.success('Screen sharing stopped');
      }
    });

    // PeerJS event listeners
    peerRef.current.on('open', (peerId) => {
      console.log('My peer ID is: ' + peerId);

      // Update own participant entry with peer ID
      setParticipants(prev =>
        prev.map(p => p.id === user.id ? { ...p, peerId } : p)
      );

      // Broadcast peer ID to other participants
      socketRef.current.emit('share-peer-id', {
        classroomId: id,
        userId: user.id,
        peerId
      });
    });

    peerRef.current.on('call', (call) => {
      if (localStreamRef.current) {
        call.answer(localStreamRef.current);
        handleIncomingCall(call);
      } else {
        // Store the call so it can be answered when the user joins
        pendingIncomingCalls.current.push(call);
      }
    });

    // If PeerJS loses its link to the signaling server (network blip), reconnect
    // while KEEPING the same peer ID so existing video links stay valid.
    peerRef.current.on('disconnected', () => {
      if (peerRef.current && !peerRef.current.destroyed) {
        try { peerRef.current.reconnect(); } catch (e) { /* already reconnecting */ }
      }
    });

    peerRef.current.on('error', (err) => {
      console.warn('PeerJS error:', err?.type || err?.message || err);
    });

    // Fetch posts
    fetchPosts();

    // Fetch students if tutor or admin
    if (classroomData && (classroomData.tutor_id === user.id || user?.user_metadata?.role === 'admin')) {
      fetchStudents();
    }

    return () => {
      // Cleanup
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (socketRef.current) {
        socketRef.current.emit('leave-classroom', id);
        socketRef.current.disconnect();
      }
      if (peerRef.current) {
        peerRef.current.destroy();
      }
    };
  }, [id, isAuthorized, isCheckingAuth]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`/api/posts/${id}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    socketRef.current.emit('classroom-message', {
      classroomId: id,
      message: newMessage
    });

    setNewMessage('');
  };

  const createPost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;

    try {
      const response = await axios.post('/api/posts', {
        classroom_id: id,
        user_id: user.id,
        content: newPost,
        type: 'text'
      });

      setPosts([response.data, ...posts]);
      setNewPost('');
      toast.success('Post created!');
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`/api/classrooms/${id}/students`);
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleInviteStudents = async (e) => {
    e.preventDefault();

    const emailList = inviteEmails
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (emailList.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }

    setSendingInvites(true);
    try {
      const response = await axios.post(`/api/classrooms/${id}/invite`, {
        emails: emailList,
        tutor_id: user.id
      });

      toast.success(`Sent ${response.data.successCount} invitation(s)!`);
      setShowInviteModal(false);
      setInviteEmails('');
    } catch (error) {
      console.error('Invite error:', error);
      toast.error(error.response?.data?.error || 'Failed to send invitations');
    } finally {
      setSendingInvites(false);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();

    if (!studentEmail.trim()) {
      toast.error('Please enter a student email');
      return;
    }

    try {
      const response = await axios.post(`/api/classrooms/${id}/students`, {
        student_email: studentEmail,
        tutor_id: user.id
      });

      toast.success(`${response.data.student.name} added to classroom!`);
      setShowAddStudentModal(false);
      setStudentEmail('');
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add student');
    }
  };

  const handleRemoveStudent = async (studentId, studentName) => {
    if (!(await confirm({
      title: 'Remove student?',
      message: `${studentName} will be removed from this classroom.`,
      confirmLabel: 'Remove',
    }))) {
      return;
    }

    try {
      await axios.delete(`/api/classrooms/${id}/students/${studentId}`, {
        data: { tutor_id: user.id }
      });

      toast.success(`${studentName} removed from classroom`);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to remove student');
    }
  };

  const startVideoCall = async () => {
    // If there's already an active call in the classroom, join it instead of starting a new one
    if (incomingCallNotification || activeCallInClassroom) {
      return joinVideoCall();
    }

    try {
      console.log('Requesting camera/microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });

      console.log('Stream obtained:', stream);
      console.log('Video tracks:', stream.getVideoTracks());
      console.log('Audio tracks:', stream.getAudioTracks());

      // Store the stream - useEffect will handle setting it to the video element
      localStreamRef.current = stream;

      // Update state to show video UI
      setInCall(true);
      setIsVideoOn(true);
      setActiveCallInClassroom(true);

      // Call all participants who have peer IDs
      console.log('Current participants:', participants);
      participants.forEach(participant => {
        if (participant.id !== user.id && participant.peerId) {
          console.log(`Calling participant ${participant.name} with peer ID ${participant.peerId}`);
          const call = peerRef.current.call(participant.peerId, stream);
          handleOutgoingCall(call);
        } else if (participant.id !== user.id && !participant.peerId) {
          console.warn(`Participant ${participant.name} doesn't have a peer ID yet`);
        }
      });

      toast.success('Video call started');

      // Notify other participants
      socketRef.current.emit('video-call-started', {
        classroomId: id,
        userId: user.id,
        userName: user.user_metadata?.name || user.email
      });
    } catch (error) {
      console.error('Error starting video call:', error);

      if (error.name === 'NotAllowedError') {
        toast.error('Camera/microphone access denied. Please allow permissions.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera/microphone found.');
      } else if (error.name === 'NotReadableError') {
        toast.error('Camera/microphone is already in use.');
      } else {
        toast.error('Failed to access camera/microphone: ' + error.message);
      }
    }
  };

  const endVideoCall = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }

    // Close all peer connections
    Object.values(peersRef.current).forEach(call => {
      if (call && call.close) call.close();
    });
    peersRef.current = {};

    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    setShowControls(true);
    setInCall(false);
    setIsVideoOn(false);
    setIsAudioOn(true);
    setPeers({});

    // Notify other participants
    socketRef.current.emit('video-call-ended', {
      classroomId: id,
      userId: user.id
    });
  };

  const joinVideoCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });

      localStreamRef.current = stream;
      setInCall(true);
      setIsVideoOn(true);
      setActiveCallInClassroom(true);
      setIncomingCallNotification(null);

      // Answer all pending incoming calls
      pendingIncomingCalls.current.forEach(call => {
        call.answer(stream);
        handleIncomingCall(call);
      });
      pendingIncomingCalls.current = [];

      // Also call any participants who have peer IDs (in case we missed their call)
      participants.forEach(participant => {
        if (participant.id !== user.id && participant.peerId && !peersRef.current[participant.peerId]) {
          const call = peerRef.current.call(participant.peerId, stream);
          handleOutgoingCall(call);
        }
      });

      // Notify server so this user is tracked as an active caller
      socketRef.current.emit('video-call-started', {
        classroomId: id,
        userId: user.id,
        userName: user.user_metadata?.name || user.email
      });

      toast.success('Joined video call');
    } catch (error) {
      console.error('Error joining video call:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Camera/microphone access denied. Please allow permissions.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera/microphone found.');
      } else {
        toast.error('Failed to join call: ' + error.message);
      }
    }
  };

  // Tell the other participants whether our camera is currently on, so they can
  // render our avatar overlay when it's off (a disabled track still sends black
  // frames over WebRTC — the receiver otherwise has no way to know it's off).
  const broadcastVideoState = () => {
    const track = localStreamRef.current?.getVideoTracks?.()[0];
    if (!track || !socketRef.current) return;
    socketRef.current.emit('video-toggle', {
      classroomId: id,
      userId: user.id,
      enabled: track.enabled
    });
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOn(videoTrack.enabled);
        broadcastVideoState();
      }
    }
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioOn(audioTrack.enabled);
      }
    }
  };

  const startScreenShare = async () => {
    try {
      // Get screen share stream
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always'
        },
        audio: false
      });

      screenShareStreamRef.current = screenStream;
      const screenTrack = screenStream.getVideoTracks()[0];

      // Handle when user stops sharing via browser UI
      screenTrack.onended = () => {
        stopScreenShare();
      };

      // Replace video track in local stream
      if (localStreamRef.current) {
        const oldVideoTrack = localStreamRef.current.getVideoTracks()[0];
        localStreamRef.current.removeTrack(oldVideoTrack);
        localStreamRef.current.addTrack(screenTrack);

        // Update local video element
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
        }

        // Replace track in all peer connections
        Object.values(peersRef.current).forEach(call => {
          if (call.peerConnection) {
            const sender = call.peerConnection.getSenders().find(s => s.track?.kind === 'video');
            if (sender) {
              sender.replaceTrack(screenTrack);
            }
          }
        });
      }

      setIsScreenSharing(true);
      setScreenSharerId(user.id);

      // Notify others that screen sharing started
      socketRef.current.emit('screen-share-started', {
        classroomId: id,
        userId: user.id,
        userName: user.user_metadata?.name || user.email
      });

      toast.success('Screen sharing started');
    } catch (error) {
      console.error('Error starting screen share:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Screen sharing permission denied');
      } else {
        toast.error('Failed to start screen sharing');
      }
    }
  };

  const stopScreenShare = async () => {
    try {
      // Stop screen share tracks
      if (screenShareStreamRef.current) {
        screenShareStreamRef.current.getTracks().forEach(track => track.stop());
        screenShareStreamRef.current = null;
      }

      // Get camera stream back
      const cameraStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false
      });

      const cameraTrack = cameraStream.getVideoTracks()[0];
      cameraTrack.enabled = isVideoOn;

      // Replace video track in local stream
      if (localStreamRef.current) {
        const oldVideoTrack = localStreamRef.current.getVideoTracks()[0];
        localStreamRef.current.removeTrack(oldVideoTrack);
        localStreamRef.current.addTrack(cameraTrack);

        // Update local video element
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
        }

        // Replace track in all peer connections
        Object.values(peersRef.current).forEach(call => {
          if (call.peerConnection) {
            const sender = call.peerConnection.getSenders().find(s => s.track?.kind === 'video');
            if (sender) {
              sender.replaceTrack(cameraTrack);
            }
          }
        });
      }

      setIsScreenSharing(false);
      setScreenSharerId(null);

      // Notify others that screen sharing stopped
      socketRef.current.emit('screen-share-stopped', {
        classroomId: id,
        userId: user.id
      });

      toast.success('Screen sharing stopped');
    } catch (error) {
      console.error('Error stopping screen share:', error);
      toast.error('Failed to stop screen sharing');
    }
  };

  const toggleFullscreen = async () => {
    if (!videoContainerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await videoContainerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
      toast.error('Failed to toggle fullscreen');
    }
  };

  // Cap the outgoing video bitrate/framerate so WebRTC doesn't saturate the
  // upstream link. An uncapped 720p stream starves the Socket.io whiteboard
  // messages, which is what makes the whiteboard lag during a call. The encoder
  // keeps full resolution when bandwidth allows and scales down under the cap.
  const limitVideoBitrate = (call) => {
    if (!call?.peerConnection) return;
    const sender = call.peerConnection.getSenders().find(s => s.track?.kind === 'video');
    if (!sender) return;
    const params = sender.getParameters();
    if (!params.encodings || params.encodings.length === 0) {
      params.encodings = [{}];
    }
    params.encodings[0].maxBitrate = 600_000; // 600 kbps
    params.encodings[0].maxFramerate = 24;
    sender.setParameters(params).catch(err => console.warn('Could not cap video bitrate:', err));
  };

  // Tear down a single remote peer's tile and connection. Used when a peer
  // leaves, refreshes, or the underlying call closes/errors — without this the
  // tile lingers as a frozen "ghost" participant.
  const removePeer = (peerId) => {
    if (!peerId) return;
    const call = peersRef.current[peerId];
    if (call && call.close) {
      try { call.close(); } catch (e) { /* already closed */ }
    }
    delete peersRef.current[peerId];
    setPeers(prev => {
      if (!(peerId in prev)) return prev;
      const next = { ...prev };
      delete next[peerId];
      return next;
    });
  };

  // Both incoming and outgoing calls key the peers map by the PeerJS peer ID
  // (call.peer). Keeping the key consistent across directions means a single
  // remote participant only ever occupies one tile — even when both sides call
  // each other (glare) during call setup.
  const registerCall = (call) => {
    peersRef.current[call.peer] = call;

    call.on('stream', (remoteStream) => {
      limitVideoBitrate(call);
      setPeers(prev => ({
        ...prev,
        [call.peer]: remoteStream
      }));
    });

    // Self-heal: drop the tile if the connection closes or errors out.
    call.on('close', () => removePeer(call.peer));
    call.on('error', () => removePeer(call.peer));
  };

  const handleIncomingCall = (call) => registerCall(call);

  const handleOutgoingCall = (call) => registerCall(call);

  const resetControlsTimer = () => {
    setShowControls(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Keep ref in sync so socket listeners can read current tab without stale closure
  useEffect(() => {
    activeTabRef.current = activeTab;
    if (activeTab === 'chat') setUnreadMessages(0);
    localStorage.setItem(`classroom-tab-${id}`, activeTab);
  }, [activeTab]);

  const handleVideoMouseDown = (e) => {
    if (isMobile) return;
    // Block drag only on buttons — allow dragging from the video surface itself
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      return;
    }

    setIsDragging(true);
    const rect = videoContainerRef.current.getBoundingClientRect();
    dragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleVideoTouchStart = (e) => {
    if (isMobile) return;
    // Block drag only on buttons — allow dragging from the video surface itself
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      return;
    }

    setIsDragging(true);
    const touch = e.touches[0];
    const rect = videoContainerRef.current.getBoundingClientRect();
    dragOffsetRef.current = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !isVideoMinimized) return;

    const newX = e.clientX - dragOffsetRef.current.x;
    const newY = e.clientY - dragOffsetRef.current.y;

    setVideoPosition({ x: newX, y: newY });
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !isVideoMinimized) return;

    const touch = e.touches[0];
    const newX = touch.clientX - dragOffsetRef.current.x;
    const newY = touch.clientY - dragOffsetRef.current.y;

    setVideoPosition({ x: newX, y: newY });
    // Prevent default to avoid scrolling while dragging
    e.preventDefault();
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Auto-expand when a peer joins so their tile has space
  useEffect(() => {
    if (Object.keys(peers).length > 0 && isVideoMinimized) {
      setIsVideoMinimized(false);
    }
  }, [Object.keys(peers).length]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging]);

  // Show loading state while checking authorization
  if (isCheckingAuth) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying classroom access...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authorized
  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="flex-1 flex overflow-hidden bg-gray-50 dark:bg-gray-900 classroom-container">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        {/* Video Call Notification */}
        {incomingCallNotification && !inCall && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg mx-2 sm:mx-4 mt-2 sm:mt-4 p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 shadow-lg">
            <div className="flex items-center space-x-3">
              <div className="bg-green-500 rounded-full p-2 animate-pulse">
                <PhoneIcon className="h-5 w-5 text-white" />
              </div>
              <span className="text-white font-medium text-sm sm:text-base">
                {incomingCallNotification.userName} started a video call
              </span>
            </div>
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              <button
                onClick={joinVideoCall}
                className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors active:scale-95"
              >
                Join Call
              </button>
              <button
                onClick={() => setIncomingCallNotification(null)}
                className="flex-1 sm:flex-none bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg font-medium transition-colors active:scale-95"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Video Area - always a floating overlay, never inline */}
        {inCall && (
          <div
            ref={videoContainerRef}
            onMouseDown={handleVideoMouseDown}
            onTouchStart={handleVideoTouchStart}
            onMouseMove={resetControlsTimer}
            onMouseEnter={resetControlsTimer}
            onTouchStartCapture={resetControlsTimer}
            className={`animate-fade-in-down z-50 bg-gray-900 overflow-hidden shadow-2xl ${
              isMobile
                ? 'fixed inset-0'
                : `fixed rounded-2xl ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} select-none`
            }`}
            style={isMobile ? {} : {
              width: isVideoMinimized ? '320px' : screenSharerId ? '544px' : '448px',
              height: isVideoMinimized ? '180px' : screenSharerId ? '357px' : '252px',
              transition: 'width 0.35s cubic-bezier(0.4,0,0.2,1), height 0.35s cubic-bezier(0.4,0,0.2,1)',
              left: videoPosition.x !== null ? `${videoPosition.x}px` : 'auto',
              top: videoPosition.y !== null ? `${videoPosition.y}px` : 'auto',
              right: videoPosition.x === null ? '1rem' : 'auto',
              bottom: videoPosition.y === null ? '5rem' : 'auto',
            }}
          >
            {/* Video content */}
            <div className={`relative w-full h-full flex flex-col ${isMobile ? '' : ''}`}>
              {/* Reconnecting banner — shown while the socket is down so the user
                  knows the call is trying to recover rather than silently frozen. */}
              {socketConnected === false && (
                <div className="absolute top-0 left-0 right-0 z-40 bg-amber-500/90 text-white text-xs font-medium py-1 px-2 flex items-center justify-center gap-1.5 pointer-events-none">
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
                  </svg>
                  Reconnecting…
                </div>
              )}
              {screenSharerId ? (
                // Screen sharing — main feed + camera thumbnail strip
                <>
                  <div className="flex-1 relative bg-gray-950 min-h-0 overflow-hidden">
                    {screenSharerId === user.id ? (
                      <>
                        <video
                          ref={localVideoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded flex items-center gap-1.5 text-xs">
                          <ComputerDesktopIcon className="h-3.5 w-3.5" />
                          <span>Your screen</span>
                        </div>
                      </>
                    ) : (
                      Object.entries(peers).map(([peerId, stream]) => {
                        const participant = participants.find(p => p.peerId === peerId);
                        if (participant?.id === screenSharerId) {
                          return (
                            <React.Fragment key={peerId}>
                              <video
                                autoPlay
                                playsInline
                                ref={el => { if (el && el.srcObject !== stream) el.srcObject = stream; }}
                                className="w-full h-full object-contain"
                              />
                              <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded flex items-center gap-1.5 text-xs">
                                <ComputerDesktopIcon className="h-3.5 w-3.5" />
                                <span>{participant.name} sharing</span>
                              </div>
                            </React.Fragment>
                          );
                        }
                        return null;
                      })
                    )}
                  </div>
                  {/* Camera thumbnail strip */}
                  {!isVideoMinimized && (
                    <div className="flex gap-1 p-1 bg-gray-900 h-16 flex-shrink-0 overflow-x-auto">
                      {screenSharerId !== user.id && (
                        <div className="relative bg-gray-800 rounded overflow-hidden aspect-video h-full flex-shrink-0">
                          <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                          {!isVideoOn && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                              <span className="text-white text-xs font-semibold select-none">
                                {(user.user_metadata?.name || user.email || '?')[0].toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="absolute bottom-0.5 left-0.5 bg-black/50 text-white px-1 rounded" style={{ fontSize: '9px' }}>You</div>
                        </div>
                      )}
                      {Object.entries(peers).map(([peerId, stream]) => {
                        const participant = participants.find(p => p.peerId === peerId);
                        if (participant?.id !== screenSharerId) {
                          const videoOff = participant && remoteVideoOff[participant.id];
                          return (
                            <div key={peerId} className="relative bg-gray-800 rounded overflow-hidden aspect-video h-full flex-shrink-0">
                              <video autoPlay playsInline ref={el => { if (el && el.srcObject !== stream) el.srcObject = stream; }} className="w-full h-full object-cover" />
                              {videoOff && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                                  <span className="text-white text-xs font-semibold select-none">
                                    {(participant.name || '?')[0].toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <div className="absolute bottom-0.5 left-0.5 bg-black/50 text-white px-1 rounded" style={{ fontSize: '9px' }}>{participant?.name || 'Guest'}</div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}
                </>
              ) : (
                // Normal camera grid
                <div className={`w-full h-full grid gap-0.5 ${
                  Object.keys(peers).length === 0 ? 'grid-cols-1' : 'grid-cols-2'
                }`}>
                  {/* Local tile */}
                  <div className="relative bg-gray-800 overflow-hidden video-tile-enter">
                    <video
                      ref={localVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                    {!isVideoOn && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10 pointer-events-none">
                        <div className={`rounded-full bg-gray-600 flex items-center justify-center ${isVideoMinimized ? 'w-8 h-8' : 'w-14 h-14'}`}>
                          <span className={`text-white font-semibold select-none ${isVideoMinimized ? 'text-sm' : 'text-xl'}`}>
                            {(user.user_metadata?.name || user.email || '?')[0].toUpperCase()}
                          </span>
                        </div>
                      </div>
                    )}
                    {!isVideoMinimized && (
                      <div className="absolute bottom-1.5 left-1.5 bg-black/50 text-white px-1.5 py-0.5 rounded text-xs z-20">
                        You
                      </div>
                    )}
                  </div>
                  {/* Remote tiles */}
                  {Object.entries(peers).map(([peerId, stream]) => {
                    const participant = participants.find(p => p.peerId === peerId);
                    const videoOff = participant && remoteVideoOff[participant.id];
                    return (
                      <div key={peerId} className="relative bg-gray-800 overflow-hidden video-tile-enter">
                        <video
                          autoPlay
                          playsInline
                          ref={el => { if (el && el.srcObject !== stream) el.srcObject = stream; }}
                          className="w-full h-full object-cover"
                        />
                        {videoOff && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10 pointer-events-none">
                            <div className={`rounded-full bg-gray-600 flex items-center justify-center ${isVideoMinimized ? 'w-8 h-8' : 'w-14 h-14'}`}>
                              <span className={`text-white font-semibold select-none ${isVideoMinimized ? 'text-sm' : 'text-xl'}`}>
                                {(participant.name || '?')[0].toUpperCase()}
                              </span>
                            </div>
                          </div>
                        )}
                        {participant && !isVideoMinimized && (
                          <div className="absolute bottom-1.5 left-1.5 bg-black/50 text-white px-1.5 py-0.5 rounded text-xs z-20">
                            {participant.name}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Controls — gradient overlay at the bottom. z-30 keeps them above
                  the camera-off avatar overlay (z-10) so they stay visible and
                  tappable when the camera is off. */}
              <div
                className={`absolute bottom-0 left-0 right-0 z-30 bg-gradient-to-t from-black/80 to-transparent pt-8 ${isMobile ? 'pb-6' : 'pb-2'} px-2 flex justify-center gap-2 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'} ${showControls ? '' : 'pointer-events-none'}`}
                onMouseDown={e => e.stopPropagation()}
              >
                <button
                  onClick={toggleVideo}
                  className={`p-2 rounded-full backdrop-blur-sm transition-all active:scale-95 text-white ${isVideoOn ? 'bg-white/20 hover:bg-white/30' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {isVideoOn ? <VideoCameraIcon className="h-4 w-4" /> : <VideoCameraSlashIcon className="h-4 w-4" />}
                </button>
                <button
                  onClick={toggleAudio}
                  className={`p-2 rounded-full backdrop-blur-sm transition-all active:scale-95 text-white ${isAudioOn ? 'bg-white/20 hover:bg-white/30' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {isAudioOn ? <MicrophoneIcon className="h-4 w-4" /> : <SpeakerXMarkIcon className="h-4 w-4" />}
                </button>
                {!isMobile && (
                  <>
                    <button
                      onClick={() => {
                        setIsVideoMinimized(!isVideoMinimized);
                        if (isVideoMinimized) setVideoPosition({ x: null, y: null });
                      }}
                      className="p-2 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white transition-all"
                      title={isVideoMinimized ? 'Expand' : 'Minimise'}
                    >
                      {isVideoMinimized ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={toggleFullscreen}
                      className="p-2 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white transition-all"
                      title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                    >
                      {isFullscreen ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
                    </button>
                  </>
                )}
                {(user?.user_metadata?.role === 'tutor' || user?.user_metadata?.role === 'admin') && supportsScreenShare && (
                  <button
                    onClick={isScreenSharing ? stopScreenShare : startScreenShare}
                    className={`p-2 rounded-full backdrop-blur-sm transition-all active:scale-95 text-white ${isScreenSharing ? 'bg-blue-500 hover:bg-blue-600' : 'bg-white/20 hover:bg-white/30'}`}
                    title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
                  >
                    {isScreenSharing ? <XMarkIcon className="h-4 w-4" /> : <ComputerDesktopIcon className="h-4 w-4" />}
                  </button>
                )}
                <button
                  onClick={endVideoCall}
                  className="p-2 rounded-full bg-red-600 hover:bg-red-700 backdrop-blur-sm text-white transition-all active:scale-95"
                >
                  <PhoneXMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Join Code Banner - Tutor/Admin Only (Collapsible) */}
        {classroomData && (classroomData.tutor_id === user.id || user?.user_metadata?.role === 'admin') && (
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-200">
            {/* Collapsed Header - Always Visible */}
            <div
              className="px-3 sm:px-6 py-2 flex items-center justify-between cursor-pointer hover:bg-primary-100 transition-colors"
              onClick={() => setIsCodeBannerExpanded(!isCodeBannerExpanded)}
            >
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-primary-900">
                  {isCodeBannerExpanded ? 'Classroom Join Code' : `Join Code: ${classroomData.code}`}
                </h3>
              </div>
              <button className="p-1 hover:bg-primary-200 rounded transition-colors">
                {isCodeBannerExpanded ? (
                  <ChevronUpIcon className="h-5 w-5 text-primary-700" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-primary-700" />
                )}
              </button>
            </div>

            {/* Expanded Content */}
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isCodeBannerExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="px-4 sm:px-6 pb-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <code className="text-xl sm:text-2xl font-mono font-bold tracking-widest text-primary-700 bg-white px-3 sm:px-4 py-2 rounded border border-primary-300 text-center sm:text-left">
                        {classroomData.code}
                      </code>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(classroomData.code);
                          }}
                          className="flex-1 sm:flex-none px-3 py-2 bg-primary-600 text-white text-sm font-medium rounded hover:bg-primary-700 transition-colors"
                        >
                          Copy Code
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(`${window.location.origin}/join/${classroomData.code}`);
                          }}
                          className="flex-1 sm:flex-none px-3 py-2 bg-white text-primary-700 border border-primary-300 text-sm font-medium rounded hover:bg-primary-50 transition-colors"
                        >
                          Copy Link
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowInviteModal(true);
                          }}
                          className="flex-1 sm:flex-none px-3 py-2 text-white text-sm font-medium rounded transition-colors"
                          style={{ backgroundColor: '#F4C70F' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#D4A800'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F4C70F'}
                        >
                          Invite Students
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="text-center lg:text-right">
                    <p className="text-xs text-primary-700">Share this code or link with students to let them join</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Tabs */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 min-h-0">
          {/* Tab Headers */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className={`flex overflow-x-auto space-x-2 sm:space-x-4 md:space-x-8 px-2 sm:px-4 md:px-6 ${isTabBarExpanded ? 'pt-3 sm:pt-4' : 'pt-2'}`}>
              <button
                onClick={() => setActiveTab('chat')}
                className={`relative ${isTabBarExpanded ? 'pb-2' : 'pb-1'} px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'chat'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                title="Chat"
              >
                <ChatBubbleLeftRightIcon className={`${isTabBarExpanded ? 'h-5 w-5' : 'h-4 w-4'} inline ${isTabBarExpanded ? 'mr-2' : ''}`} />
                {isTabBarExpanded && 'Chat'}
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('posts')}
                className={`${isTabBarExpanded ? 'pb-2' : 'pb-1'} px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'posts'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                title="Posts"
              >
                <PaperClipIcon className={`${isTabBarExpanded ? 'h-5 w-5' : 'h-4 w-4'} inline ${isTabBarExpanded ? 'mr-2' : ''}`} />
                {isTabBarExpanded && 'Posts'}
              </button>
              <button
                onClick={() => setActiveTab('files')}
                className={`${isTabBarExpanded ? 'pb-2' : 'pb-1'} px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'files'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                title="Files"
              >
                <FolderIcon className={`${isTabBarExpanded ? 'h-5 w-5' : 'h-4 w-4'} inline ${isTabBarExpanded ? 'mr-2' : ''}`} />
                {isTabBarExpanded && 'Files'}
              </button>
              <button
                onClick={() => setActiveTab('whiteboard')}
                className={`${isTabBarExpanded ? 'pb-2' : 'pb-1'} px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'whiteboard'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                title="Whiteboard"
              >
                <PencilIcon className={`${isTabBarExpanded ? 'h-5 w-5' : 'h-4 w-4'} inline ${isTabBarExpanded ? 'mr-2' : ''}`} />
                {isTabBarExpanded && 'Whiteboard'}
              </button>
              <button
                onClick={() => setActiveTab('schedule')}
                className={`${isTabBarExpanded ? 'pb-2' : 'pb-1'} px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'schedule'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                title="Schedule"
              >
                <CalendarDaysIcon className={`${isTabBarExpanded ? 'h-5 w-5' : 'h-4 w-4'} inline ${isTabBarExpanded ? 'mr-2' : ''}`} />
                {isTabBarExpanded && 'Schedule'}
              </button>
              {classroomData && (classroomData.tutor_id === user.id || user?.user_metadata?.role === 'admin') && (
                <button
                  onClick={() => setActiveTab('students')}
                  className={`${isTabBarExpanded ? 'pb-2' : 'pb-1'} px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'students'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                  title="Students"
                >
                  <UserGroupIcon className={`${isTabBarExpanded ? 'h-5 w-5' : 'h-4 w-4'} inline ${isTabBarExpanded ? 'mr-2' : ''}`} />
                  {isTabBarExpanded && `Students (${students.length})`}
                </button>
              )}
              {!inCall && (
                <button
                  onClick={startVideoCall}
                  className={`ml-auto ${isTabBarExpanded ? 'pb-2 px-3 sm:px-4' : 'pb-1 px-2'} text-white rounded-t-lg active:scale-95 ${
                    activeCallInClassroom
                      ? 'bg-green-600 hover:bg-green-700 animate-pulse'
                      : 'bg-primary-600 hover:bg-primary-700'
                  }`}
                  title={activeCallInClassroom ? 'Join Active Call' : 'Start Video Call'}
                >
                  <VideoCameraIcon className={`${isTabBarExpanded ? 'h-5 w-5' : 'h-4 w-4'} inline ${isTabBarExpanded ? 'mr-1 sm:mr-2' : ''}`} />
                  {isTabBarExpanded && (
                    <span className="hidden sm:inline">{activeCallInClassroom ? 'Join Active Call' : 'Start Video Call'}</span>
                  )}
                  {isTabBarExpanded && (
                    <span className="sm:hidden">{activeCallInClassroom ? 'Join' : 'Call'}</span>
                  )}
                </button>
              )}
              <div
                className={`flex items-center ${isTabBarExpanded ? 'pb-2' : 'pb-1'} px-1`}
                title={socketConnected === null ? 'Connecting...' : socketConnected ? 'Connected' : 'Disconnected — trying to reconnect'}
              >
                <div className={`w-2 h-2 rounded-full ${
                  socketConnected === null ? 'bg-gray-400' :
                  socketConnected ? 'bg-green-500' :
                  'bg-red-500 animate-pulse'
                }`} />
                {!socketConnected && socketConnected !== null && isTabBarExpanded && (
                  <span className="ml-1.5 text-xs text-red-500">Reconnecting...</span>
                )}
              </div>
              <button
                onClick={() => setIsTabBarExpanded(!isTabBarExpanded)}
                className={`ml-auto ${isTabBarExpanded ? 'pb-2' : 'pb-1'} px-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300`}
                title={isTabBarExpanded ? 'Collapse tabs' : 'Expand tabs'}
              >
                {isTabBarExpanded ? <ChevronLeftIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
            {activeTab === 'chat' ? (
              <div className="flex flex-col h-full">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-3 sm:space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.userId === user.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        msg.userId === user.id
                          ? 'bg-primary-600 dark:bg-primary-700 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}>
                        <p className="text-xs font-semibold mb-1">
                          {msg.userName}
                        </p>
                        <p className="text-sm">{msg.message}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={sendMessage} className="border-t border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex space-x-4">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Type your message..."
                    />
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600"
                    >
                      <PaperAirplaneIcon className="h-5 w-5" />
                    </button>
                  </div>
                </form>
              </div>
            ) : activeTab === 'posts' ? (
              <div className="flex flex-col h-full">
                {/* Posts */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-3 sm:space-y-4">
                  {posts.map((post) => (
                    <div key={post.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <div className="bg-primary-100 dark:bg-primary-900 p-2 rounded-full">
                          <UserGroupIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{post.user?.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(post.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <p className="text-gray-800 dark:text-gray-200">{post.content}</p>
                    </div>
                  ))}
                </div>

                {/* Create Post */}
                {(user?.user_metadata?.role === 'tutor' || user?.user_metadata?.role === 'admin') && (
                  <form onSubmit={createPost} className="border-t border-gray-200 dark:border-gray-700 p-4">
                    <textarea
                      value={newPost}
                      onChange={(e) => setNewPost(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      rows="3"
                      placeholder="Share something with the class..."
                    />
                    <button
                      type="submit"
                      className="mt-2 px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600"
                    >
                      Post
                    </button>
                  </form>
                )}
              </div>
            ) : activeTab === 'files' ? (
              <div className="flex flex-col h-full">
                {/* Files Tab */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Classroom Files</h3>

                  {/* Upload Section - For tutors and admins */}
                  {(user?.user_metadata?.role === 'tutor' || user?.user_metadata?.role === 'admin') && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Upload File</h4>
                      <FileUpload
                        classroomId={id}
                        userId={user.id}
                        onUploadSuccess={() => setFileRefreshKey(k => k + 1)}
                      />
                    </div>
                  )}

                  {/* Files List */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">All Files</h4>
                    <FileList
                      classroomId={id}
                      userId={user.id}
                      userRole={user?.user_metadata?.role || 'student'}
                      refreshTrigger={fileRefreshKey}
                    />
                  </div>
                </div>
              </div>
            ) : activeTab === 'whiteboard' ? (
              <div className="h-full w-full">
                {socketRef.current ? (
                  <DocumentWorkspace
                    socket={socketRef.current}
                    classroomId={id}
                    userId={user.id}
                    userRole={user?.user_metadata?.role || 'student'}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Connecting...</p>
                    </div>
                  </div>
                )}
              </div>
            ) : activeTab === 'schedule' ? (
              <div className="h-full w-full">
                {socketRef.current ? (
                  <Schedule
                    classroomId={id}
                    userId={user.id}
                    userRole={user?.user_metadata?.role || 'student'}
                    socket={socketRef.current}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Connecting...</p>
                    </div>
                  </div>
                )}
              </div>
            ) : activeTab === 'students' ? (
              <div className="flex-1 overflow-auto p-3 sm:p-6">
                <div className="max-w-4xl mx-auto">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Enrolled Students</h2>
                    <button
                      onClick={() => setShowAddStudentModal(true)}
                      className="px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white font-medium rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
                    >
                      <PlusIcon className="h-5 w-5 inline mr-2" />
                      Add Student Manually
                    </button>
                  </div>

                  {students.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-lg font-medium text-gray-900">No students enrolled yet</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Share your classroom code or send email invitations to get started.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white shadow-md rounded-lg overflow-hidden overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Student
                            </th>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                              Email
                            </th>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                              Enrolled
                            </th>
                            <th className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {students.map((enrollment) => (
                            <tr key={enrollment.id} className="hover:bg-gray-50">
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 bg-primary-100 rounded-full flex items-center justify-center">
                                    <span className="text-primary-700 font-semibold text-sm sm:text-base">
                                      {enrollment.student?.name?.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="ml-2 sm:ml-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {enrollment.student?.name}
                                    </div>
                                    {/* Show email under name on mobile */}
                                    <div className="text-xs text-gray-500 sm:hidden">
                                      {enrollment.student?.email}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden sm:table-cell">
                                <div className="text-sm text-gray-500">{enrollment.student?.email}</div>
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden md:table-cell">
                                <div className="text-sm text-gray-500">
                                  {new Date(enrollment.enrolled_at).toLocaleDateString()}
                                </div>
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleRemoveStudent(enrollment.student_id, enrollment.student?.name)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Participants Sidebar - Hidden on mobile */}
      <div
        className={`bg-gray-50 dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out overflow-hidden flex-shrink-0 ${
          isMobile ? 'hidden' : isSidebarOpen ? 'w-64' : 'w-10'
        }`}
      >
        {/* Collapsed/Expanded Header */}
        <div
          className="px-3 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          {isSidebarOpen ? (
            <>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                Participants ({participants.length})
              </h3>
              <ChevronRightIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
            </>
          ) : (
            <div className="flex flex-col items-center w-full">
              <UserGroupIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 mt-1">{participants.length}</span>
            </div>
          )}
        </div>

        {/* Participants List */}
        <div className={`p-4 ${isSidebarOpen ? 'opacity-100' : 'opacity-0 hidden'} transition-opacity duration-300`}>
          <div className="space-y-2">
            {participants.map((participant) => (
              <div key={participant.id} className="flex items-center p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                <div className="bg-primary-100 dark:bg-primary-900 p-2 rounded-full flex-shrink-0">
                  <UserGroupIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="ml-3 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{participant.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{participant.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Invite Students Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Invite Students via Email</h2>

            <form onSubmit={handleInviteStudents}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Student Email Addresses
                </label>
                <textarea
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows="4"
                  placeholder="student1@example.com, student2@example.com"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Separate multiple emails with commas
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmails('');
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingInvites}
                  className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#F4C70F' }}
                  onMouseEnter={(e) => !sendingInvites && (e.currentTarget.style.backgroundColor = '#D4A800')}
                  onMouseLeave={(e) => !sendingInvites && (e.currentTarget.style.backgroundColor = '#F4C70F')}
                >
                  {sendingInvites ? 'Sending...' : 'Send Invitations'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Student Manually Modal */}
      {showAddStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md">
            <h2 className="text-lg sm:text-xl font-bold mb-4">Add Student Manually</h2>

            <form onSubmit={handleAddStudent}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Student Email
                </label>
                <input
                  type="email"
                  value={studentEmail}
                  onChange={(e) => setStudentEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="student@example.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Student must have an account on Solomon
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddStudentModal(false);
                    setStudentEmail('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Add Student
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Classroom;
